import React, { useState } from 'react';
import { Button, makeStyles, Tooltip } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import NavigationDrawer from './NavigationDrawer/NavigationDrawer';
import Logo from './Logo/Logo';
import { Box } from '@material-ui/core';
import CustomButton from 'components/CustomButtons/Button';
import { fetchLabels } from 'utils/labels/labels';
import { useDispatch, useSelector } from 'react-redux';
import { getLanguage } from 'utils/language/language';
import { setLanguage } from 'redux/modules/settings/language/actions';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/layouts/pageHeaderStyle';
import PageContainer from '../PageContainer/PageContainer';
import { dashRoutes } from 'routes/dashboardRoutes';
import TopNavigation from 'components/Navbars/TopNavigation/TopNavigation';
import Settings from './Settings/Settings';
import {
	getNavigationsLinksLabels,
	getLanguageSwitcherLabels,
	getNotificationsLabels,
} from 'redux/modules/dynamicLabels/selectors';
import NotificationPanel from '../Notifications/NotificationPanel';
import ProfileDetailsDropdown from 'components/ProfileDetailsDropdown/ProfileDetailsDropdown';
import bell from 'assets/img/icons/bell.svg';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { upIdSelector } from 'redux/modules/session/selectors';
import { UserGuideURL } from 'config/app.config';
import { isUserGuideOn } from 'utils/killswitch';
// @ts-ignore
const useStyles = makeStyles(styles);

export type PageHeaderProps = {
	userType: string;
	remoteConfig: string;
	accountType: types.routes.accountType;
	pathname: string;
};

const mapViews = (views: types.routes.ISingleDashboardRoute[], linksLabels: Record<string, string>) => {
	return views.map((view) => ({
		...view,
		name: linksLabels[view.name],
	}));
};

const PageHeader = ({ accountType, remoteConfig, userType, pathname }: PageHeaderProps) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const changeLanguageTo: types.redux.settings.language = getLanguage() === 'ar' ? 'en' : 'ar';
	const languageSwitcherLabels = useSelector(getLanguageSwitcherLabels);
	const [showNotification, setShowNotification] = useState(false);
	const notificationsLabels = useSelector(getNotificationsLabels);
	const accId = useSelector(upIdSelector);
	const linksLabels = useSelector(getNavigationsLinksLabels);
	const isUserGuideEnabled = isUserGuideOn(remoteConfig);

	const hideNotification = () => {
		setShowNotification(false);
	};

	const handleRouteClick = (navigationType: string, routeName: string, parentName?: string) => {
		setDataLayer(navigationType, getLanguage(), null, accId, null, {
			link_name: parentName ? `${parentName}:${routeName}` : routeName,
		});
	};

	const routes = dashRoutes({ accountType, userType, remoteConfig })
		.filter((route) => {
			return route.visible;
		})
		.map((route) => ({
			...route,
			name: linksLabels[route.name],
			views: mapViews(route?.views || [], linksLabels),
		}));

	return (
		<header className={classes.header}>
			<PageContainer>
				<nav aria-label="Main Navigation">
					<Grid container direction="row" justify="flex-start" alignItems="center">
						<Box display={{ xs: 'block', lg: 'none' }}>
							<NavigationDrawer>
								<TopNavigation
									routes={routes}
									pathname={pathname}
									handleRouteClick={handleRouteClick}
								/>
							</NavigationDrawer>
						</Box>
						<Box className={classes.logo}>
							<Logo />
						</Box>
						<Box display={{ xs: 'none', lg: 'flex' }} className={classes.menu}>
							<TopNavigation routes={routes} pathname={pathname} handleRouteClick={handleRouteClick} />
						</Box>
						<Box display="flex" alignItems="center">
							<Box display={{ xs: 'none', lg: 'block' }}>
								<CustomButton
									color="primary-dark-v2"
									onClick={() => {
										fetchLabels(changeLanguageTo);
										dispatch(setLanguage(changeLanguageTo));
										handleRouteClick(
											'secondary_navigation',
											languageSwitcherLabels?.switchLanguage
										);
									}}
									noMargin
									aria-label={languageSwitcherLabels?.switchLanguage}
								>
									{translate(`auth.${changeLanguageTo}`)}
								</CustomButton>
							</Box>
							{isUserGuideEnabled && (
								<Box display={{ xs: 'none', lg: 'block' }}>
									<Tooltip placement="bottom" title={translate('help')}>
										<Button
											href={UserGuideURL}
											target="_blank"
											onClick={() => handleRouteClick('secondary_navigation', 'Help')}
										>
											<img
												src="/questionMarkCircle.svg"
												height={24}
												width={24}
												alt=""
												role="presentation"
											/>
										</Button>
									</Tooltip>
								</Box>
							)}
							<Box display={{ xs: 'none', lg: 'block' }}>
								<Settings />
							</Box>
							<Box display={{ xs: 'none', lg: 'block' }}>
								<Button
									onClick={(): void => {
										setShowNotification(true);
										handleRouteClick('secondary_navigation', notificationsLabels?.notifications);
									}}
									data-testid="drawer"
									aria-label={notificationsLabels?.notifications}
								>
									<img src={bell} height={24} width={24} alt="" role="presentation" />
								</Button>
								<NotificationPanel show={showNotification} hideNotification={hideNotification} />
							</Box>
							<Box>
								<ProfileDetailsDropdown handleRouteClick={handleRouteClick} />
							</Box>
						</Box>
					</Grid>
				</nav>
			</PageContainer>
		</header>
	);
};

export default PageHeader;
